import { Dayjs } from 'dayjs';
import { array, boolean, mixed, object, string } from 'yup';

import { graphemeLength } from './utils';

import { PartnerSpotlightStatusesEnum } from 'v2/constants';

export const BONUS_RECEIVED_MAX = 35;
export const EXTRA_OFFER_MAX = 25;
export const LOTTERY_DESCRIPTION_MAX = 50;
export const LOTTERY_PROMO_MAX = 15;
export const PARTNER_SPOTLIGHT_IMAGE_MAX_SIZE = 5 * 1024 * 1024;

export interface LotteryFormSection {
  imageId: string;
  description: string;
  promo1: string;
  promo2: string;
  endDate: Dayjs | null;
}

export interface PartnerSpotlightForm {
  partnerId: string;
  status: PartnerSpotlightStatusesEnum;
  backgroundImageId: string;
  isBackgroundOnly: boolean;
  hasNewBadge: boolean;
  bonusReceivedText: string;
  extraOfferText: string;
  promoCode: string;
  lotteryEnabled: boolean;
  lottery: LotteryFormSection;
}

export const emptyLottery: LotteryFormSection = {
  imageId: '',
  description: '',
  promo1: '',
  promo2: '',
  endDate: null,
};

export const initialValues: PartnerSpotlightForm = {
  partnerId: '',
  status: PartnerSpotlightStatusesEnum.BRONZE,
  backgroundImageId: '',
  isBackgroundOnly: false,
  hasNewBadge: false,
  bonusReceivedText: '',
  extraOfferText: '',
  promoCode: '',
  lotteryEnabled: false,
  lottery: emptyLottery,
};

const graphemeMax = (max: number, message: string) =>
  string().test('grapheme-length', message, (v) => graphemeLength(v ?? '') <= max);

export const validationSchema = object().shape({
  partnerId: string().required(),
  status: mixed<PartnerSpotlightStatusesEnum>().oneOf(Object.values(PartnerSpotlightStatusesEnum)).required(),
  backgroundImageId: string().required(),
  isBackgroundOnly: boolean().required(),
  hasNewBadge: boolean().required(),
  bonusReceivedText: graphemeMax(BONUS_RECEIVED_MAX, `Up to ${BONUS_RECEIVED_MAX} characters`),
  extraOfferText: graphemeMax(EXTRA_OFFER_MAX, `Up to ${EXTRA_OFFER_MAX} characters`),
  promoCode: string(),
  lotteryEnabled: boolean().required(),
  lottery: object().when('lotteryEnabled', {
    is: true,
    then: (schema) =>
      schema.shape({
        imageId: string().required('Picture is required'),
        description: graphemeMax(LOTTERY_DESCRIPTION_MAX, `Up to ${LOTTERY_DESCRIPTION_MAX} characters`).required(
          'Description is required'
        ),
        promo1: graphemeMax(LOTTERY_PROMO_MAX, `Up to ${LOTTERY_PROMO_MAX} characters`),
        promo2: graphemeMax(LOTTERY_PROMO_MAX, `Up to ${LOTTERY_PROMO_MAX} characters`),
        endDate: mixed()
          .required('End date is required')
          .test('is-future', 'End date must be in the future', (value) => {
            if (!value) return false;
            const d = value as Dayjs;
            return d.isValid() && d.isAfter(new Date());
          }),
      }),
    otherwise: (schema) => schema.strip(),
  }),
  promoTexts: array().of(string()).optional(),
});
